import { Helmet } from "react-helmet-async";
import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
// @mui
import {
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Container,
  Typography,
  CircularProgress,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
} from "@mui/material";
import Scrollbar from "../components/scrollbar";
import Iconify from "../components/iconify";
import { get } from "src/utils/networkUtils";
import { fDateTime } from "src/utils/formatTime";
import { getPhaseName } from "src/components/hammer-smash/HammerSmashTable";
import GrantHammersDialog from "src/components/hammer-smash/GrantHammersDialog";
// ----------------------------------------------------------------------

const linkSx = {
  textDecoration: 'none',
  color: 'primary.main',
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
};

export default function HammerSmashParticipantsPage() {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const [loading, setLoading] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [total, setTotal] = useState(0);
  const [campaign, setCampaign] = useState(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [grantOpen, setGrantOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const observer = useRef();
  const limit = 20;

  useEffect(() => {
    if (campaignId) {
      get(`/admin/hammer-smash/${campaignId}`)
        .then(function (response) {
          setCampaign(response.campaign);
        })
        .catch(function (error) {
          console.error('Error fetching hammer smash campaign:', error);
        });
    }
  }, [campaignId, reloadKey]);

  useEffect(() => {
    if (campaignId) {
      setLoading(true);
      get(`/admin/hammer-smash/${campaignId}/participants?offset=${offset}&limit=${limit}`)
        .then(function (response) {
          setParticipants((prev) =>
            offset === 0
              ? response.users || []
              : [...prev, ...(response.users || [])]
          );
          setTotal(response.total || 0);
          setHasMore((response.users || []).length === limit);
          setLoading(false);
        })
        .catch(function (error) {
          console.error('Error fetching hammer smash participants:', error);
          setLoading(false);
        });
    }
  }, [campaignId, offset, reloadKey]);

  const handleUserProfile = (userId) => {
    navigate(`/dashboard/profile?user_id=${userId}`);
  };

  const handleGranted = () => {
    setGrantOpen(false);
    setOffset(0);
    setReloadKey((prev) => prev + 1);
  };

  const lastParticipantRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setOffset((prev) => prev + limit);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore, limit]
  );

  const canGrant = campaign && !campaign.drawnAt && new Date() < new Date(campaign.drawDate);

  return (
    <>
      <Helmet>
        <title>Hammer Smash Participants</title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <div>
            <Typography variant="h4" gutterBottom>
              Hammer Smash Participants
            </Typography>
            {campaign && (
              <Typography variant="h6" color="text.secondary">
                {campaign.title}
              </Typography>
            )}
          </div>
          <Box>
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:email-outline" />}
              onClick={() => setGrantOpen(true)}
              disabled={!canGrant}
              sx={{ mr: 1 }}
            >
              Grant postal entry
            </Button>
            <Button
              variant="outlined"
              startIcon={<Iconify icon="eva:arrow-back-fill" />}
              onClick={() => navigate('/dashboard/hammer-smash')}
            >
              Back to Hammer Smash
            </Button>
          </Box>
        </Stack>

        {campaign && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2}>
                {campaign.prizeImage && (
                  <Grid item xs={12} sm={3}>
                    <Box
                      component="img"
                      src={campaign.prizeImage}
                      alt={campaign.prizeTitle}
                      sx={{ maxWidth: "100%", maxHeight: 160, borderRadius: 1 }}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={campaign.prizeImage ? 9 : 12}>
                  <Typography variant="subtitle1">{campaign.prizeTitle}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {campaign.prizeDescription}
                  </Typography>
                  <Typography variant="body2">Phase: {getPhaseName(campaign.phase)}</Typography>
                  <Typography variant="body2">
                    Participants: {total} · Hammers issued: {campaign.hammersCount}
                  </Typography>
                  <Typography variant="body2">Draw date: {fDateTime(campaign.drawDate)}</Typography>
                  {campaign.drawnAt ? (
                    <>
                      <Typography variant="body2">
                        Drawn at {fDateTime(campaign.drawnAt)} out of {campaign.totalHammersAtDraw} hammers
                      </Typography>
                      {campaign.winnerUserId ? (
                        <Typography variant="body2">
                          Winner:{" "}
                          <Link component="button" variant="body2" sx={linkSx} onClick={() => handleUserProfile(campaign.winnerUserId)}>
                            {campaign.winnerUsername}
                          </Link>{" "}
                          (hammer #{campaign.winningHammerId}) ·{" "}
                          {campaign.winningHammerSmashed ? "Case opened" : "Not smashed yet"}
                        </Typography>
                      ) : (
                        <Typography variant="body2">No hammers were issued, so there is no winner.</Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">Not drawn yet</Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        <Card>
          <Scrollbar>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="participants table">
                <TableHead>
                  <TableRow>
                    <TableCell>User ID</TableCell>
                    <TableCell>Username</TableCell>
                    <TableCell>Purchase Redemptions</TableCell>
                    <TableCell>Postal Entries</TableCell>
                    <TableCell>Hammers</TableCell>
                    <TableCell>Smashed</TableCell>
                    <TableCell>Remaining</TableCell>
                    <TableCell>Winner</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {participants.length === 0 && !loading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No participants yet
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    participants.map((participant, index) => (
                      <TableRow
                        key={participant.userId}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                        ref={
                          index === participants.length - 1
                            ? lastParticipantRef
                            : null
                        }
                      >
                        <TableCell component="th" scope="row">
                          {participant.userId}
                        </TableCell>
                        <TableCell>
                          <Link
                            component="button"
                            variant="body2"
                            onClick={() => handleUserProfile(participant.userId)}
                            sx={linkSx}
                          >
                            {participant.username}
                          </Link>
                        </TableCell>
                        <TableCell>{participant.purchaseRedemptions}</TableCell>
                        <TableCell>{participant.postalRedemptions}</TableCell>
                        <TableCell>{participant.hammers}</TableCell>
                        <TableCell>{participant.smashed}</TableCell>
                        <TableCell>{participant.remaining}</TableCell>
                        <TableCell>
                          {participant.isWinner && <Chip label="Winner" color="success" size="small" />}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Card>
      </Container>

      <GrantHammersDialog
        open={grantOpen}
        campaign={campaign}
        onClose={() => setGrantOpen(false)}
        onGranted={handleGranted}
      />
    </>
  );
}
